/**
 * V13 铸剑阁进度服务 — 逐级解锁
 *
 * 设计准绳：conversation plan（逐章逐关解锁）
 * 数据存储：localStorage（key: battlecode-forge-progress）
 * 集成点：screen-12-forge.ts（读取）+ screen-5-result.ts（写入）+ gm-tool.ts（绕过）
 */

import { CHAPTERS, type ChapterData } from "../render/forge/types";

// ===== 类型 =====

export interface ForgeProgressData {
  version: 1;
  chapters: Record<number, ForgeChapterProgressData>;
  gmUnlockAll: boolean;
}

export interface ForgeChapterProgressData {
  completedLevels: string[];
  examCompleted: boolean;
  examRank?: string;
}

const STORAGE_KEY = "battlecode-forge-progress";

// ===== 读写 =====

export function loadForgeProgress(): ForgeProgressData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, chapters: {}, gmUnlockAll: false };
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1) return { version: 1, chapters: {}, gmUnlockAll: false };
    return parsed as ForgeProgressData;
  } catch {
    return { version: 1, chapters: {}, gmUnlockAll: false };
  }
}

export function saveForgeProgress(data: ForgeProgressData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

// ===== 核心逻辑 =====

/** 获取某章的进度数据（不存在则创建） */
function ensureChapter(progress: ForgeProgressData, chapterNum: number): ForgeChapterProgressData {
  if (!progress.chapters[chapterNum]) {
    progress.chapters[chapterNum] = { completedLevels: [], examCompleted: false };
  }
  return progress.chapters[chapterNum];
}

/** 获取章节模板数据 */
function getChapterTemplate(chapterNum: number): ChapterData | undefined {
  return CHAPTERS.find((c) => c.num === chapterNum);
}

/** 获取某章所有关卡 ID（不含 exam） */
function getAllLevelIds(ch: ChapterData): string[] {
  return ch.sections.flatMap((s) => s.items.map((item) => item.id));
}

/** 判断某章是否所有关卡+考试已完成 */
function isChapterCompleted(chapterNum: number, progress: ForgeProgressData): boolean {
  if (progress.gmUnlockAll) return true;
  const ch = getChapterTemplate(chapterNum);
  if (!ch) return false;
  const cp = progress.chapters[chapterNum];
  if (!cp) return false;
  const allLevelIds = getAllLevelIds(ch);
  const hasExam = !!ch.exam;
  const levelsDone = allLevelIds.every((id) => cp.completedLevels.includes(id));
  const examDone = !hasExam || cp.examCompleted;
  return levelsDone && examDone;
}

/** 战斗胜利时标记关卡完成 */
export function markForgeLevelCompleted(
  chapterNum: number,
  levelId: string
): { chapterCompleted: boolean; chapterNum: number } {
  const progress = loadForgeProgress();
  const cp = ensureChapter(progress, chapterNum);

  // 记录关卡完成
  if (!cp.completedLevels.includes(levelId)) {
    cp.completedLevels.push(levelId);
  }

  // 标记考试完成
  if (levelId.startsWith("exam-")) {
    cp.examCompleted = true;
  }

  const chapterCompleted = isChapterCompleted(chapterNum, progress);
  saveForgeProgress(progress);

  return { chapterCompleted, chapterNum };
}

/** 动态计算 5 章的运行时状态（注入 state/progress/level states） */
export function computeChapterStates(): ChapterData[] {
  const progress = loadForgeProgress();
  // 深拷贝模板数据
  const result: ChapterData[] = JSON.parse(JSON.stringify(CHAPTERS));

  for (const ch of result) {
    const cp = progress.chapters[ch.num];

    // 确定章状态
    if (progress.gmUnlockAll) {
      ch.state = "completed";
    } else if (ch.num === 1) {
      ch.state = isChapterCompleted(1, progress) ? "completed" : "current";
    } else {
      const prevCompleted = isChapterCompleted(ch.num - 1, progress);
      if (!prevCompleted) {
        ch.state = "locked";
      } else {
        ch.state = isChapterCompleted(ch.num, progress) ? "completed" : "current";
      }
    }

    // 计算进度
    const totalLevels = getAllLevelIds(ch).length + (ch.exam ? 1 : 0);
    let doneCount = 0;
    if (progress.gmUnlockAll) {
      doneCount = totalLevels;
    } else if (cp) {
      getAllLevelIds(ch).forEach((id) => {
        if (cp.completedLevels.includes(id)) doneCount++;
      });
      if (ch.exam && cp.examCompleted) doneCount++;
    }
    ch.progress = { done: doneCount, total: totalLevels };

    // 设置关卡状态
    applyLevelStates(ch, cp, progress.gmUnlockAll);

    // 设置考试状态
    if (ch.exam) {
      if (progress.gmUnlockAll) {
        ch.exam.state = "completed";
        ch.exam.rank = cp?.examRank ?? ch.exam.rank;
      } else if (ch.state === "locked") {
        ch.exam.state = "locked";
      } else if (ch.state === "completed") {
        ch.exam.state = "completed";
      } else {
        // chapter is "current"
        const allLevelIds = getAllLevelIds(ch);
        const allLevelsDone = allLevelIds.every((id) => cp?.completedLevels.includes(id));
        if (allLevelsDone) {
          ch.exam.state = cp?.examCompleted ? "completed" : "available";
        } else {
          ch.exam.state = "locked";
        }
      }
      if (cp?.examRank) {
        ch.exam.rank = cp.examRank;
      }
    }
  }

  return result;
}

/** 设置某章关卡的运行时状态 */
function applyLevelStates(
  ch: ChapterData,
  cp: ForgeChapterProgressData | undefined,
  gmUnlockAll: boolean
): void {
  if (gmUnlockAll) {
    ch.sections.forEach((sec) => {
      sec.items.forEach((item) => {
        item.state = "completed";
      });
    });
    return;
  }

  if (ch.state === "locked") {
    ch.sections.forEach((sec) => {
      sec.items.forEach((item) => {
        item.state = "locked";
      });
    });
    return;
  }

  // chapter is "current" or "completed" — sequential unlock
  let firstUncompletedFound = false;
  for (const sec of ch.sections) {
    for (const item of sec.items) {
      const isCompleted = cp?.completedLevels.includes(item.id) ?? false;
      if (isCompleted || ch.state === "completed") {
        item.state = "completed";
      } else if (!firstUncompletedFound) {
        item.state = "current";
        firstUncompletedFound = true;
      } else {
        item.state = "locked";
      }
    }
  }
}

// ===== GM 工具 =====

export function gmUnlockAllChapters(): void {
  const progress = loadForgeProgress();
  progress.gmUnlockAll = true;
  saveForgeProgress(progress);
}

export function gmUnlockChapter(chapterNum: number): void {
  const progress = loadForgeProgress();
  progress.gmUnlockAll = false;
  // 标记本章所有关卡+考试已完成
  const ch = getChapterTemplate(chapterNum);
  if (!ch) return;
  const cp = ensureChapter(progress, chapterNum);
  getAllLevelIds(ch).forEach((id) => {
    if (!cp.completedLevels.includes(id)) cp.completedLevels.push(id);
  });
  cp.examCompleted = !!ch.exam;
  saveForgeProgress(progress);
}

export function gmResetForgeProgress(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function gmResetChapter(chapterNum: number): void {
  const progress = loadForgeProgress();
  progress.gmUnlockAll = false;
  delete progress.chapters[chapterNum];
  saveForgeProgress(progress);
}
